import { useEffect, useState } from "react";
import type { Translate } from "@shared/relative-strings";
import {
  enablePush,
  isInstalled,
  pushPermission,
  pushSupported,
  type PushState,
} from "./push";

/**
 * NotifyPrompt - the onboarding upsell for the background ring (VC-23).
 *
 * Shown on the landing screen AFTER a first successful visit, only where the
 * room can actually ring this phone. It leads with WHY in plain language - "so
 * you hear when {name} calls" - never a bare permission ask, because the
 * audience is family, often 50-70, and a naked "Allow notifications?" from a
 * page they opened once gets declined on reflex.
 *
 * Two shapes, decided by the device:
 *
 *  - **Android / installed PWA** - Web Push is available, so offer the one-tap
 *    enable. A denial is terminal (the OS will not re-prompt) and is met with a
 *    quiet "you can turn it on in settings", not a nag.
 *  - **iOS Safari, not installed** - Web Push does not exist until the site is
 *    on the home screen, so there is no button to press; the card explains the
 *    Share → "Add to Home Screen" step instead. This is the documented,
 *    accepted iOS limitation: for those relatives SMS (VC-24) is the real ring
 *    path, and this card is how we recover the ones who will install.
 *
 * Either way it is dismissible and remembers the dismissal, so it asks once and
 * then gets out of the way. It never gates the call - the SMS still arrives.
 */

const DISMISS_KEY = "relative.notifyPromptDismissed";

type Mode = "hidden" | "enable" | "install" | "granted" | "denied";

function initialMode(): Mode {
  // Already on - a small confirmation, then it self-dismisses.
  const perm = pushPermission();
  if (perm === "granted") return "granted";

  let dismissed = false;
  try {
    dismissed = localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    /* private mode - treat as not dismissed */
  }
  if (dismissed) return "hidden";

  if (pushSupported()) return perm === "denied" ? "denied" : "enable";

  // No Push API. On an iOS device that is not yet installed, home-screen
  // install is the path to it; on anything else (a desktop tab), stay silent.
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent || "");
  if (isIos && !isInstalled()) return "install";
  return "hidden";
}

function remember(): void {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* ignore */
  }
}

interface Props {
  t: Translate;
  /** Resident first name, possibly "" - the copy falls back to a generic line. */
  residentName: string;
  locale: string;
}

export function NotifyPrompt({ t, residentName }: Props) {
  const [mode, setMode] = useState<Mode>("hidden");
  const [busy, setBusy] = useState(false);

  // Decide on mount only (client APIs are not available during SSR and the
  // decision must not flip while the relative reads it).
  useEffect(() => {
    setMode(initialMode());
  }, []);

  // A "granted" confirmation is transient - show it briefly, then fold away.
  useEffect(() => {
    if (mode !== "granted") return;
    const id = window.setTimeout(() => setMode("hidden"), 4000);
    return () => window.clearTimeout(id);
  }, [mode]);

  if (mode === "hidden") return null;

  const why = residentName
    ? t("notif_why", { name: residentName })
    : t("notif_why_generic");
  const iosSteps = residentName
    ? t("install_ios_steps", { name: residentName })
    : t("install_ios_steps_generic");

  const onEnable = async () => {
    setBusy(true);
    const state: PushState = await enablePush();
    setBusy(false);
    if (state === "granted") {
      setMode("granted");
    } else if (state === "denied") {
      setMode("denied");
    } else if (state === "unsupported") {
      setMode("hidden");
    }
    // "default" (dismissed the OS prompt) - leave the card as-is to try again.
  };

  const onDismiss = () => {
    remember();
    setMode("hidden");
  };

  if (mode === "granted") {
    return (
      <div className="notify notify--ok" role="status">
        <span className="notify__tick" aria-hidden="true">
          ✓
        </span>
        <p className="notify__body">{t("notif_on")}</p>
      </div>
    );
  }

  return (
    <section className="notify" aria-label={t("notif_title")}>
      <h2 className="notify__title">
        {mode === "install" ? t("install_title") : t("notif_title")}
      </h2>
      <p className="notify__body">{mode === "install" ? iosSteps : why}</p>

      {mode === "enable" && (
        <div className="notify__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onEnable}
            disabled={busy}
          >
            {t("notif_enable")}
          </button>
          <button
            type="button"
            className="notify__later"
            onClick={onDismiss}
            disabled={busy}
          >
            {t("notif_later")}
          </button>
        </div>
      )}

      {mode === "denied" && (
        <>
          <p className="notify__hint">{t("notif_denied")}</p>
          <div className="notify__actions">
            <button type="button" className="notify__later" onClick={onDismiss}>
              {t("dismiss")}
            </button>
          </div>
        </>
      )}

      {mode === "install" && (
        <div className="notify__actions">
          <button type="button" className="notify__later" onClick={onDismiss}>
            {t("dismiss")}
          </button>
        </div>
      )}
    </section>
  );
}
